import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';

/**
 * Progress, completion and reminders for the 7-daagse challenge.
 *
 * Progress and completions live in the database (challenge_progress /
 * challenge_completions) so they follow the user across devices and can back a
 * certificate. The reminder switch stays in localStorage — it is a property of
 * the device, not the account.
 */

/** A day is completed by its meditation. */
export type DayPart = 'meditation';

export type DayProgress = Record<DayPart, boolean>;

export type ChallengeProgress = Record<number, DayProgress>;

export interface ChallengeCompletion {
  id: string;
  challenge_slug: string;
  completed_at: string;
  certificate_sent_at: string | null;
}

const key = (slug: string, name: string) => `challenge:${slug}:${name}`;

export function useChallengeProgress(slug: string, totalDays: number) {
  const { profile } = useProfile();
  const userId = profile?.id ?? null;

  const [progress, setProgress] = useState<ChallengeProgress>({});
  const [completion, setCompletion] = useState<ChallengeCompletion | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const [{ data: rows }, { data: done }] = await Promise.all([
      supabase
        .from('challenge_progress')
        .select('day, part')
        .eq('user_id', userId)
        .eq('challenge_slug', slug),
      supabase
        .from('challenge_completions')
        .select('id, challenge_slug, completed_at, certificate_sent_at')
        .eq('user_id', userId)
        .eq('challenge_slug', slug)
        .maybeSingle(),
    ]);

    const mapped: ChallengeProgress = {};
    for (const row of rows ?? []) {
      const current = mapped[row.day] ?? { meditation: false };
      mapped[row.day] = { ...current, [row.part as DayPart]: true };
    }

    setProgress(mapped);
    setCompletion(done ?? null);
    setLoading(false);
  }, [slug, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const isComplete = (state: ChallengeProgress) => {
    for (let day = 1; day <= totalDays; day++) {
      if (!state[day]?.meditation) return false;
    }
    return true;
  };

  const toggle = useCallback(
    async (day: number, part: DayPart) => {
      if (!userId) return;

      const current = progress[day] ?? { meditation: false };
      const next: ChallengeProgress = {
        ...progress,
        [day]: { ...current, [part]: !current[part] },
      };

      // optimistic — a failed write is corrected by the reload below
      setProgress(next);

      if (current[part]) {
        await supabase
          .from('challenge_progress')
          .delete()
          .eq('user_id', userId)
          .eq('challenge_slug', slug)
          .eq('day', day)
          .eq('part', part);
      } else {
        await supabase.from('challenge_progress').insert({
          user_id: userId,
          challenge_slug: slug,
          day,
          part,
        });
      }

      // Finishing the last box earns the certificate. Once earned it stays:
      // un-ticking a day later does not take it away.
      if (isComplete(next) && !completion) {
        const { data } = await supabase
          .from('challenge_completions')
          .insert({ user_id: userId, challenge_slug: slug })
          .select('id, challenge_slug, completed_at, certificate_sent_at')
          .single();
        if (data) setCompletion(data);
      }
    },
    [completion, progress, slug, totalDays, userId],
  );

  /** Clears the ticks so the challenge can be walked again. Keeps the certificate. */
  const reset = useCallback(async () => {
    if (!userId) return;
    setProgress({});
    await supabase
      .from('challenge_progress')
      .delete()
      .eq('user_id', userId)
      .eq('challenge_slug', slug);
  }, [slug, userId]);

  const markCertificateSent = useCallback(async () => {
    if (!completion) return;
    const sentAt = new Date().toISOString();
    setCompletion({ ...completion, certificate_sent_at: sentAt });
    await supabase
      .from('challenge_completions')
      .update({ certificate_sent_at: sentAt })
      .eq('id', completion.id);
  }, [completion]);

  const completedDays = Object.values(progress).filter(
    (d) => d.meditation,
  ).length;

  // The day the user is "on": the first one not fully ticked off.
  const currentDay = (() => {
    for (let day = 1; day <= totalDays; day++) {
      if (!progress[day]?.meditation) return day;
    }
    return totalDays;
  })();

  return {
    progress,
    toggle,
    reset,
    completedDays,
    currentDay,
    completion,
    markCertificateSent,
    loading,
  };
}

/** Every challenge the signed-in user has finished — used on the profile page. */
export function useChallengeCompletions() {
  const { profile } = useProfile();
  const userId = profile?.id ?? null;
  const [completions, setCompletions] = useState<ChallengeCompletion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    supabase
      .from('challenge_completions')
      .select('id, challenge_slug, completed_at, certificate_sent_at')
      .eq('user_id', userId)
      .order('completed_at', { ascending: false })
      .then(({ data }) => {
        setCompletions(data ?? []);
        setLoading(false);
      });
  }, [userId]);

  return { completions, loading };
}

/**
 * Which introduction sections the signed-in user has listened to, so they can
 * be folded away afterwards. Stored per account rather than per device, so
 * reading them on a phone leaves them folded on a laptop too.
 */
export function useIntroHeard(slug: string) {
  const { profile } = useProfile();
  const userId = profile?.id ?? null;

  const [heard, setHeard] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    supabase
      .from('challenge_intro_heard')
      .select('section_id')
      .eq('user_id', userId)
      .eq('challenge_slug', slug)
      .then(({ data }) => {
        setHeard(new Set((data ?? []).map((row) => row.section_id)));
        setLoading(false);
      });
  }, [slug, userId]);

  const markHeard = useCallback(
    async (sectionId: string) => {
      if (!userId || heard.has(sectionId)) return;

      setHeard((prev) => new Set(prev).add(sectionId));
      await supabase.from('challenge_intro_heard').insert({
        user_id: userId,
        challenge_slug: slug,
        section_id: sectionId,
      });
    },
    [heard, slug, userId],
  );

  return { heard, markHeard, loading };
}

/** The affirmations someone picked for a given day of a challenge. */
export function useAffirmations(slug: string, day: number) {
  const { profile } = useProfile();
  const userId = profile?.id ?? null;

  const [chosen, setChosen] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!userId) return;

    supabase
      .from('challenge_affirmations')
      .select('affirmation_id')
      .eq('user_id', userId)
      .eq('challenge_slug', slug)
      .eq('day', day)
      .then(({ data }) => {
        setChosen(new Set((data ?? []).map((row) => row.affirmation_id)));
      });
  }, [day, slug, userId]);

  const toggle = useCallback(
    async (affirmationId: string) => {
      if (!userId) return;
      const isChosen = chosen.has(affirmationId);

      setChosen((prev) => {
        const next = new Set(prev);
        if (isChosen) next.delete(affirmationId);
        else next.add(affirmationId);
        return next;
      });

      if (isChosen) {
        await supabase
          .from('challenge_affirmations')
          .delete()
          .eq('user_id', userId)
          .eq('challenge_slug', slug)
          .eq('day', day)
          .eq('affirmation_id', affirmationId);
      } else {
        await supabase.from('challenge_affirmations').insert({
          user_id: userId,
          challenge_slug: slug,
          day,
          affirmation_id: affirmationId,
        });
      }
    },
    [chosen, day, slug, userId],
  );

  return { chosen, toggle };
}

export type ReminderState = 'unsupported' | 'off' | 'on' | 'blocked';

/**
 * Daily reminder at a fixed time.
 *
 * NOTE: this uses the browser's Notification API, which can only fire while the
 * app is actually open. Reminders that arrive with the app closed need
 * `@capacitor/local-notifications` in the native builds.
 */
export function useDailyReminder(slug: string, time: string, message: string) {
  const enabledKey = key(slug, 'reminder');
  const supported = typeof window !== 'undefined' && 'Notification' in window;

  // on by default: someone who wants the daily nudge shouldn't have to find
  // the switch. Stored only once they change it.
  const [enabled, setEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem(enabledKey) !== 'false';
    } catch {
      return true;
    }
  });
  const [permission, setPermission] = useState<NotificationPermission | null>(
    supported ? Notification.permission : null,
  );

  const state: ReminderState = !supported
    ? 'unsupported'
    : permission === 'denied'
      ? 'blocked'
      : enabled
        ? 'on'
        : 'off';

  const setReminder = useCallback(
    async (next: boolean) => {
      if (next && supported && Notification.permission === 'default') {
        const result = await Notification.requestPermission();
        setPermission(result);
        if (result !== 'granted') return;
      }
      setEnabled(next);
      try {
        localStorage.setItem(enabledKey, String(next));
      } catch {
        // private mode — the switch just won't be remembered
      }
    },
    [enabledKey, supported],
  );

  useEffect(() => {
    if (!enabled || !supported || Notification.permission !== 'granted') return;

    let timer: ReturnType<typeof setTimeout>;

    const msUntilNext = () => {
      const [hours, minutes] = time.split(':').map(Number);
      const next = new Date();
      next.setHours(hours, minutes, 0, 0);
      if (next.getTime() <= Date.now()) next.setDate(next.getDate() + 1);
      return next.getTime() - Date.now();
    };

    const schedule = () => {
      timer = setTimeout(() => {
        new Notification('Even stilstaan', { body: message });
        schedule();
      }, msUntilNext());
    };

    schedule();
    return () => clearTimeout(timer);
  }, [enabled, supported, time, message]);

  return { state, setReminder };
}
